import React, { useMemo } from 'react';
import Spinner from 'react-bootstrap/Spinner';

const DEFAULT_SUMMARY = [
  { key: 'inProgress',   label: 'Em Andamento',        color: '#2b19ca' },
  { key: 'critical',     label: 'Críticas > 3h',       color: '#d32f2f' },
  { key: 'revalidation', label: 'Em Revalidação',      color: '#fa8006' },
  { key: 'approved',     label: 'Aprovadas',           color: '#2e7d32' },
  { key: 'notStarted',   label: 'Não iniciadas',       color: '#90a4ae' },
];

const norm = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const timeTextToMinutes = (txt) => {
  if (!txt) return 0;
  const s = String(txt).toLowerCase();

  const d = /(\d+)\s*d/.exec(s);
  const h = /(\d+)\s*h/.exec(s);
  const m = /(\d+)\s*m/.exec(s);

  const days  = d ? parseInt(d[1], 10) : 0;
  const hours = h ? parseInt(h[1], 10) : 0;
  const mins  = m ? parseInt(m[1], 10) : 0;

  return (days * 24 * 60) + (hours * 60) + mins;
};

const isCriticalByDetails = (item) => {
  const arr = item?.details?.temposPorSetor;
  if (!Array.isArray(arr)) return false;
  for (const t of arr) {
    const mins = timeTextToMinutes(t?.time);
    if (mins >= 180) return true;
  }
  return false;
};

const inferStatusKey = (item, statusKeyProp = 'statusKey') => {
  const direct = item?.[statusKeyProp];
  if (direct) return direct;

  const tag = norm(item?.header?.statusTag);
  if (!tag) return null;

  if (tag.includes('revalida')) return 'revalidation';
  if (tag.includes('aprov'))    return 'approved';
  if (tag.includes('andament') || tag.includes('pendente') || tag.includes('validacao')) {
    return 'inProgress';
  }
  if (tag.includes('nao inici') || tag.includes('não inici')) return 'notStarted';

  return null;
};

export default function Box({
  cards = [],
  asLegend,
  className,
  style,
  legendFontSize = 16,
  legendGap = 14,    
  legendDot = 18,     
  labelStyle,
  data,
  statusKeyProp = 'statusKey',
  minCard = 220,
  gap = 12,
  dense = true,
  cardDot = 16,    
  cardTitleSize = 18,
  cardValueSize = 30,
  computeCounts,
  counts: countsFromApi,
  loading = false,
}) {
  const effectiveCards = !asLegend && (!cards || cards.length === 0)
    ? DEFAULT_SUMMARY
    : cards;

  const derivedCounts = useMemo(() => {
    if (!data || !data.length) return {};

    if (typeof computeCounts === 'function') {
      const res = computeCounts(data, effectiveCards);
      return res && typeof res === 'object' ? res : {};
    }

    const keys = new Set(effectiveCards.map(c => c.key));
    const map = {};

    for (const item of data) {
      if (keys.has('critical') && isCriticalByDetails(item)) {
        map.critical = (map.critical || 0) + 1;
      }
      const k = inferStatusKey(item, statusKeyProp);
      if (k && keys.has(k)) {
        map[k] = (map[k] || 0) + 1;
      }
    }

    return map;
  }, [data, effectiveCards, statusKeyProp, computeCounts]);

  const finalCounts = countsFromApi || derivedCounts;

  if (asLegend) {
    return (
      <div className={className} style={{ padding: '0 12px 0', ...style }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            className="d-flex flex-wrap align-items-center"
            style={{
              display: 'inline-flex',
              flexWrap: 'wrap',
              gap: 16,
              borderBottom: '1px solid #e1e5eb',
              paddingBottom: 8,
            }}
          >
            {effectiveCards.map((c, idx) => {
              const isTitle = c.isTitle || c.key === 'legendTitle';
              return (
                <div
                  key={c.key}
                  className="d-flex align-items-center"
                  style={{ gap: isTitle ? 8 : legendGap }}
                >
                  {isTitle ? (
                    <span style={{ fontSize: legendFontSize + 1, fontWeight: 700, color: '#111' }}>
                      {c.label}
                    </span>
                  ) : (
                    <>
                      {c.color && (
                        <span
                          style={{
                            display: 'inline-block',
                            width: legendDot,
                            height: legendDot,
                            borderRadius: legendDot / 2,
                            background: c.dot || c.color,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: legendFontSize,
                          lineHeight: `${legendFontSize + 5}px`,
                          fontWeight: 600,
                          color: isTitle ? '#111' : '#607d8b',
                          ...labelStyle,
                        }}
                      >
                        {c.label}
                      </span>
                    </>
                  )}
                  {idx < effectiveCards.length - 1 && !isTitle && (
                    <span style={{ color: '#cfd8dc', padding: '0 4px' }}>|</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
  const cardPad = dense ? 14 : 18;
  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minCard}px, 1fr))`,
        gap,
        ...style,
      }}
    >
      {effectiveCards.map((c) => {
        const hasValue = !loading && finalCounts && Object.prototype.hasOwnProperty.call(finalCounts, c.key);
        const value = hasValue ? Number(finalCounts[c.key] ?? 0) : null;
        return (
          <div
            key={c.key}
            style={{
              border: '1px solid #e1e5eb',
              borderRadius: 10,
              padding: cardPad,
              background: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: dense ? 72 : 88,
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: cardDot,
                  height: cardDot,
                  borderRadius: cardDot / 2,
                  background: c.dot || c.color,
                }}
              />
              <span style={{ fontSize: cardTitleSize, color: '#607d8b', fontWeight: 600 }}>
                {c.label}
              </span>
            </div>
            <div
              style={{
                minWidth: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: cardValueSize,
                fontWeight: 800,
                color: c.color,
                lineHeight: 1,
              }}
            >
              {hasValue ? (
                value
              ) : (
                <Spinner animation="border" role="status" size="sm" style={{ color: c.color }} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
export { DEFAULT_SUMMARY, timeTextToMinutes, isCriticalByDetails, inferStatusKey };